import {AfterViewInit, Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {StateConfigService} from "../../../../services/state/state-config.service";
import {ActivatedRoute, Router} from "@angular/router";
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";
import {QualityCheck, QualityControllerService, Rule} from "../../../../api";
import {
  faChevronRight,
  faCircleInfo,
  faCircleXmark,
  faPlusCircle,
  faSquareDashedCirclePlus
} from "@fortawesome/pro-solid-svg-icons";
import {PermissionService} from "../../../../services/permission/permission.service";
import {initFlowbite} from "flowbite";
import {QualityStepperComponent} from "../quality-stepper/quality-stepper.component";

import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {MatIconButton} from "@angular/material/button";
import {BicBadgeComponent, BicButtonComponent, TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {BicFilterComponent, BicFilterConfig} from "../../../util/design/bic-filter/bic-filter.component";
import {QualityRulesPrefetchService} from "../../../../services/prefetch/quality-rules-prefetch.service";
import {FilePrefetchService} from "../../../../services/prefetch/file-prefetch.service";
import {TooltipSizeDirective} from "../../../../directives/tooltip-size.directive";

@Component({
  selector: 'app-quality-choose-rules',
  templateUrl: './quality-choose-rules.component.html',
  styleUrls: ['./quality-choose-rules.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    QualityStepperComponent,
    CdkDropList,
    FaIconComponent,
    MatIconButton,
    TooltipDirective,
    TooltipSizeDirective,
    BicButtonComponent,
    CdkDrag,
    BicBadgeComponent,
    BicFilterComponent
  ]
})
export class QualityChooseRulesComponent implements OnInit, AfterViewInit {
  stateConfigService = inject(StateConfigService);
  permissionService = inject(PermissionService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private qualityControllerService = inject(QualityControllerService);
  rulesLoaded = false;
  protected readonly faSpinnerThird = faSpinnerThird;

  check!: QualityCheck
  rules: Rule[] = [];
  private qualityRulesPrefetchService = inject(QualityRulesPrefetchService);
  selectedRules: Rule[] = [];
  filterRules: Rule[] = [];

  ifcFiles: Rule[] = [];
  lvFiles: Rule[] = [];
  roomBookFiles: Rule[] = [];

  docTypeMap: Record<string, string> = {
    'IFC': 'IFC',
    'Leistungsverzeichnis (LV)': 'LV',
    'Raumbuch (RB)': 'RB'
  };

  categoryMap: Record<string, string> = {
    'Arbeitsstättenrichtlinie (ASR)': 'ASR',
    'DIN-Normen': 'DIN',
    'Fabrikplanung': 'Fabrikplanung',
    'Grundwasserverordnung (GRWV)': 'GRWV',
    'Musterbauordnung (MBO)': 'MBO'
  }

  filtersConfig: BicFilterConfig[] = [
    {
      key: "category",
      displayName: "Prüfstandard",
      options: Object.keys(this.categoryMap),
      type: 'text'
    },
    {
      key: "docType",
      displayName: "Dokumententyp",
      options: Object.keys(this.docTypeMap),
      type: 'text'
    }
  ]

  constructor() {
  }

  ngAfterViewInit() {
    initFlowbite()
  }
  private currentFilterConfig: { selectedDocTypes: string[], selectedCategories: string[] } = {
    selectedDocTypes: [],
    selectedCategories: []
  };

  get filteredRules(): Rule[] {
    return this.filterRules;
  }

  filtersChange(event: { [p: string]: any[] }) {
    const selectedDocTypes = (event['docType'] ?? []).map(value => this.docTypeMap[value] ?? value);
    const selectedCategories = (event['category'] ?? []).map(value => this.categoryMap[value] ?? value);

    // Speichere die aktuelle Filter-Konfiguration
    this.currentFilterConfig = {selectedDocTypes, selectedCategories};

    if (selectedDocTypes.length === 0 && selectedCategories.length === 0) {
      this.filterRules = [...this.rules];
      return;
    }

    this.filterRules = this.rules.filter(rule => {
      const docTypeMatch = () => {
        if (selectedDocTypes.length === 0) {
          return true;
        }
        return rule.docTypes?.map(t => t.name).some(docType => selectedDocTypes.includes(docType!)) ?? false;
      };

      const categoryMatch = () => {
        if (selectedCategories.length === 0) {
          return true;
        }
        return selectedCategories.includes(rule.name?.split(" ")[0] ?? '');
      }

      return docTypeMatch() && categoryMatch();
    });
  }
  private filePrefetchService = inject(FilePrefetchService);

  isRuleSelected(rule: Rule): boolean {
    return this.selectedRules.some(r => r.id === rule.id);
  }

  addRuleToSelectedRules(rule: Rule) {
    if (!this.selectedRules.some(r => r.id === rule.id)) {
      this.selectedRules.push(rule);
    }
    this.rules = this.rules.filter(r => r.id !== rule.id);
    this.filterRules = this.filterRules.filter(r => r.id !== rule.id);
  }

  removeRuleFromSelectedRules(rule: Rule) {
    this.selectedRules = this.selectedRules.filter(selectedRule => selectedRule.id !== rule.id);
    this.rules.push(rule);
    this.rules.sort((a, b) => a.name!.localeCompare(b.name!));

    // Aktualisiere filterRules basierend auf dem aktuellen Filter
    this.updateFilterRules();
  }

  addOrRemoveFilteredRulesFromSelectedRules() {
    const allVisibleRulesSelected = this.filterRules.length === 0 ||
      this.filterRules.every(rule => this.selectedRules.some(selected => selected.id === rule.id));

    if (allVisibleRulesSelected) {
      if (this.filterRules.length === 0) {
        const rulesToRemove = this.selectedRules.filter(rule => this.wouldRuleMatchCurrentFilter(rule));

        rulesToRemove.forEach(rule => {
          // Entferne von selectedRules
          this.selectedRules = this.selectedRules.filter(r => r.id !== rule.id);
          // Füge zu rules hinzu
          if (!this.rules.some(r => r.id === rule.id)) {
            this.rules.push(rule);
          }
        });

        this.rules.sort((a, b) => a.name!.localeCompare(b.name!));
        this.updateFilterRules();
      } else {
        // Normale Deselektierung: Entferne alle filterRules von selectedRules
        const filterRuleIds = this.filterRules.map(r => r.id);
        this.selectedRules = this.selectedRules.filter(rule => !filterRuleIds.includes(rule.id));

        // Füge sie zurück zu rules
        this.filterRules.forEach(rule => {
          if (!this.rules.some(r => r.id === rule.id)) {
            this.rules.push(rule);
          }
        });
        this.rules.sort((a, b) => a.name!.localeCompare(b.name!));
        this.updateFilterRules();
      }
    } else {
      // SELEKTIEREN: Füge alle gefilterten Regeln zu selectedRules hinzu
      this.filterRules.forEach(rule => {
        if (!this.selectedRules.some(r => r.id === rule.id)) {
          this.selectedRules.push(rule);
        }
      });

      // Entferne sie von rules
      const filterRuleIds = this.filterRules.map(r => r.id);
      this.rules = this.rules.filter(rule => !filterRuleIds.includes(rule.id));
      this.filterRules = [];
    }
  }

  drop(event: CdkDragDrop<Rule[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const rule = event.previousContainer.data[event.previousIndex];

      // Bestimme, ob die Regel vom Container filterRules oder selectedRules kommt
      // basierend auf der Container-ID
      const previousContainerId = event.previousContainer.id;
      const currentContainerId = event.container.id;

      // Wenn von 'availableRulesList' zu 'selectedRulesList'
      if (previousContainerId === 'availableRulesList' && currentContainerId === 'selectedRulesList') {
        this.addRuleToSelectedRules(rule);
      }
      // Wenn von 'selectedRulesList' zu 'availableRulesList'
      else if (previousContainerId === 'selectedRulesList' && currentContainerId === 'availableRulesList') {
        this.removeRuleFromSelectedRules(rule);
      }
    }
  }

  getRuleInfoTooltip(rule: Rule) {
    return rule.hint + '<br><span class=\'font-medium\'>Überprüfte Bauteile:</span> ' + rule.parts + '<br><span class=\'font-medium\'>Notwendige Nutzerangaben:</span> ' + rule.parametersText + '<br><span class=\'font-medium\'>Notwendige Modellinformationen:</span> ' + rule.required_model_information + '<br><span class=\'font-medium\'>Überprüfte Dokumententypen:</span> ' + rule.docTypesText;
  }

  stepperEvent(index: number) {
    if (index == 0) {
      this.navigateToChoosingFiles()
    }
  }

  navigateToChoosingFiles() {
    this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'quality', 'choose-files'], {
      state: {
        ifcFiles: this.ifcFiles,
        lvFiles: this.lvFiles,
        roomBookFiles: this.roomBookFiles,
        selectedRules: this.selectedRules
      }
    });
  }

  private updateFilterRules(): void {
    // Wenn es einen aktiven Filter gibt, wende ihn erneut an
    // Sonst setze filterRules gleich rules
    if (this.hasActiveFilter()) {
      this.applyCurrentFilter();
    } else {
      this.filterRules = [...this.rules];
    }
  }

  private hasActiveFilter(): boolean {
    // Prüfe ob filterRules weniger Elemente hat als rules (bedeutet Filter ist aktiv)
    return this.filterRules.length < this.rules.length && this.rules.length > 0;
  }

  private applyCurrentFilter(): void {
    const {selectedDocTypes, selectedCategories} = this.currentFilterConfig;

    this.filterRules = this.rules.filter(rule => {
      const docTypeMatch = () => {
        if (selectedDocTypes.length === 0) {
          return true;
        }
        return rule.docTypes?.map(t => t.name).some(docType => selectedDocTypes.includes(docType!)) ?? false;
      };

      const categoryMatch = () => {
        if (selectedCategories.length === 0) {
          return true;
        }
        return selectedCategories.includes(rule.name?.split(" ")[0] ?? '');
      }

      return docTypeMatch() && categoryMatch();
    });
  }

  private wouldRuleMatchCurrentFilter(rule: Rule): boolean {
    const {selectedDocTypes, selectedCategories} = this.currentFilterConfig;

    // Wenn keine Filter aktiv sind, matched jede Regel
    if (selectedDocTypes.length === 0 && selectedCategories.length === 0) {
      return true;
    }

    const docTypeMatch = () => {
      if (selectedDocTypes.length === 0) {
        return true;
      }
      return rule.docTypes?.map(t => t.name).some(docType => selectedDocTypes.includes(docType!)) ?? false;
    };

    const categoryMatch = () => {
      if (selectedCategories.length === 0) {
        return true;
      }
      return selectedCategories.includes(rule.name?.split(" ")[0] ?? '');
    }

    return docTypeMatch() && categoryMatch();
  }

  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faPlusCircle = faPlusCircle;
  protected readonly faSquareDashedCirclePlus = faSquareDashedCirclePlus;
  protected readonly faCircleInfo = faCircleInfo;

  ngOnInit(): void {
    this.check = this.activatedRoute.snapshot.data.qualityCheck;

    // Prefetch files in background
    this.filePrefetchService.prefetch();

    this.ifcFiles = this.router.getCurrentNavigation()?.extras?.state!.ifcFiles ?? [];
    this.lvFiles = this.router.getCurrentNavigation()?.extras?.state!.lvFiles ?? [];
    this.roomBookFiles = this.router.getCurrentNavigation()?.extras?.state!.roomBookFiles ?? [];
    this.selectedRules = this.router.getCurrentNavigation()?.extras?.state!.selectedRules ?? [];

    // Use prefetch service (caches across navigations)
    this.qualityRulesPrefetchService.getRules().subscribe(rules => {
      this.rules = rules;
      // remove already selected rules from rules list
      this.selectedRules.forEach(selectedRule => {
        this.rules = this.rules.filter(rule => rule.id !== selectedRule.id);
      });
      // Aktualisiere filterRules mit allen verfügbaren Regeln
      this.filterRules = [...this.rules];
      this.rulesLoaded = true;
    });
  }
}
